import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type Toast = { id: number; text: string; tone: 'info' | 'success' | 'danger' };

type ToastContextValue = {
  show: (text: string, tone?: Toast['tone']) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const show = useCallback((text: string, tone: Toast['tone'] = 'info') => {
    const id = nextId.current++;
    setToasts((arr) => [...arr, { id, text, tone }]);
    setTimeout(() => setToasts((arr) => arr.filter((t) => t.id !== id)), 1500);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex flex-col items-center gap-2 px-4">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={[
              'pointer-events-auto rounded-md px-3 py-1.5 text-[13px] font-medium',
              t.tone === 'danger' ? 'text-danger' : t.tone === 'success' ? 'text-success' : 'text-ink-muted',
            ].join(' ')}
          >
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}

export function useTransientError(err: unknown): void {
  const toast = useToast();
  useEffect(() => {
    if (err) {
      const msg = err instanceof Error ? err.message : 'Xatolik yuz berdi';
      toast.show(msg, 'danger');
    }
  }, [err, toast]);
}
